import { html, nothing } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";

import "@phosphor-icons/webcomponents/PhCheckCircle";
import "@phosphor-icons/webcomponents/PhPlus";
import { SignalElement } from "ui-common/di";

import "../form-elements/input/wow-input.ts";
import "../skeleton-rect";

import type { CreatorItem, ICreatorSelectState, ICreatorSelectService } from "ui-common/services/creator-select/types";
import { styles } from "./creator-select-list.css";

export abstract class CreatorSelectList extends SignalElement {
  static override styles = [styles];

  protected abstract selectState: ICreatorSelectState;
  protected abstract selectService: ICreatorSelectService;
  protected abstract get creators(): CreatorItem[];
  protected abstract get isLoading(): boolean;
  protected abstract onSearch(query: string): void;

  @property({ type: String })
  placeholder = "Search creators...";

  @property({ type: String })
  addLabel = "Add";

  @property({ type: String })
  emptyTitle = "No creators found";

  @property({ type: String })
  emptySubtitle = "Try adjusting your search terms";

  private searchDebounceTimeout?: ReturnType<typeof setTimeout>;
  private imageErrors = new Set<string>();

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
  }

  private handleSearchInput(e: Event) {
    const input = e.target as HTMLInputElement;
    const value = input.value;

    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }

    this.searchDebounceTimeout = setTimeout(() => {
      this.onSearch(value);
    }, 300);
  }

  private handleCardClick(item: CreatorItem) {
    this.selectService.toggleSelection(item);
    this.onSelectionChanged();
  }

  protected onSelectionChanged(): void {
    // Subclasses can override to react to selection changes
  }

  private handleImageError(creatorId: string) {
    this.imageErrors.add(creatorId);
    this.requestUpdate();
  }

  private getCombinedCreators(): CreatorItem[] {
    const selectedItems = this.selectState.selectedItems.get();
    const selectedIds = this.selectState.selectedIds.get();
    const creators = this.creators;

    const combinedMap = new Map<string, CreatorItem>();

    for (const item of selectedItems) {
      combinedMap.set(item.id, item);
    }

    for (const item of creators) {
      if (!combinedMap.has(item.id)) {
        combinedMap.set(item.id, item);
      }
    }

    const combined = Array.from(combinedMap.values());

    combined.sort((a, b) => {
      const aSelected = selectedIds.has(a.id);
      const bSelected = selectedIds.has(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });

    return combined;
  }

  private renderCreatorCard(item: CreatorItem) {
    const isSelected = this.selectState.selectedIds.get().has(item.id);
    const hasImageError = this.imageErrors.has(item.id);

    return html`
      <div class="creator-card" @click=${() => this.handleCardClick(item)}>
        <div class="creator-card-image">
          ${item.imageUrl && !hasImageError
            ? html`<img
                src=${item.imageUrl}
                alt=${item.handle || "Creator"}
                @error=${() => this.handleImageError(item.id)}
              />`
            : html`<div class="creator-card-placeholder">${item.handle?.charAt(0)?.toUpperCase() || "?"}</div>`}
        </div>
        <div class="creator-card-info">
          <div class="creator-card-handle">@${item.handle || "unknown"}</div>
        </div>
        <div class="creator-card-follow">
          <div class="follow-action">
            ${isSelected
              ? html`<ph-check-circle weight="bold"></ph-check-circle>`
              : html`
                  <ph-plus></ph-plus>
                  <span>${this.addLabel}</span>
                `}
          </div>
        </div>
      </div>
    `;
  }

  private renderList() {
    const combinedCreators = this.getCombinedCreators();
    const loading = this.isLoading;

    const loadingTemplate = repeat(
      Array(2),
      () => html`<skeleton-rect class="skeleton-rectangle" width="100%" height="56px"></skeleton-rect>`,
    );

    if (combinedCreators.length === 0 && !loading) {
      return html`
        <div class="empty-state">
          <div class="empty-state-title">${this.emptyTitle}</div>
          <div class="empty-state-subtitle">${this.emptySubtitle}</div>
        </div>
      `;
    }

    return html`
      <div class="list">
        ${repeat(
          combinedCreators,
          (item) => item.id,
          (item) => this.renderCreatorCard(item),
        )}
        ${loading ? loadingTemplate : nothing}
      </div>
    `;
  }

  protected override render() {
    return html`
      <div class="creator-select">
        <div class="search-container">
          <wow-input placeholder=${this.placeholder} @input=${this.handleSearchInput}></wow-input>
        </div>
        ${this.renderList()}
      </div>
    `;
  }
}
